import {
  Middleware,
  Reducer,
  configureStore,
  combineReducers,
} from '@reduxjs/toolkit';
import {
  TypedUseSelectorHook,
  useDispatch as untypedUseDispatch,
  useSelector as untypedUseSelector,
} from 'react-redux';

import { userContextSlice } from '@tdp/common/auth';
import { authenticatedImageSlice, createAuthenticatedImageCommandHandler } from '@tdp/common/authenticatedImages';
import { createCientSwitchCommandHandler } from '@tdp/common/clientSwitch';
import {
  combineHandlers,
  combineActionSources,
} from '@tdp/common/commandHandler';
import { headerSlice, createHeaderCommandHandler } from '@tdp/common/header';
import {
  localizationSlice,
  createLocalizationCommandHandler,
} from '@tdp/common/localization';
import { lroSlice } from '@tdp/common/lro';
import { notificationSlice } from '@tdp/common/notification';
import { settingsSlice } from '@tdp/common/settings';
import { createSettingsCommandHandler } from '@tdp/common/settings/commands';
import { immediate } from '@tdp/common/shared';
import { PromisedReturnType } from '@tdp/common/shared/tsUtils';
import {
  themeSlice,
  createThemeCommandHandler,
  createThemeEventSource,
} from '@tdp/common/theme';
import { createAdaptiveCardCommandHandler } from '@tdp/modules/adaptiveCards/redux/commands';
import { adaptiveCardSlice } from '@tdp/modules/adaptiveCards/redux/slice';
import {
  analyticsFilterSlice,
  analyticsWidgetsSlice,
  appDetailsSlice,
  createAppDetailCommandHandler,
  createAppAnalyticsCommandHandler,
  analyticsV2MetricsSlice,
  analyticsV1MetricsSlice,
  appListSlice,
  createAppListCommandHandler,
  appValidationSlice,
  createAppValidationCommandHandler,
  appPublishingSlice,
  appTypeSlice
} from '@tdp/modules/apps';
import { createPackageEditorCommandHandler } from '@tdp/modules/apps/packageEditor/redux/commands';
import { packageEditorSlice } from '@tdp/modules/apps/packageEditor/redux/slice';
import { createPeoplePickerCommands } from '@tdp/modules/apps/peoplePicker/peoplePickerCommands';
import { peoplePickerSlice } from '@tdp/modules/apps/peoplePicker/peoplePickerSlice';
import { botSlice, createBotCommandHandler } from '@tdp/modules/bots';
import { botEventSubscriptionSlice, createBotEventSubscriptionCommandHandler } from '@tdp/modules/bots';

import { Router } from './routes';
import { DevPortalServices } from './services';

// This must be a separate function and have no inferred types based on the root state
// failure to do so will result in the RootState getting bottomed out to any or a type error
// due to circular definition.

function composeReducers() {
  //TODO(nibeauli): create type-safe way of adding additional (non-slice) reducers.
  return combineReducers({
    [lroSlice.name]: lroSlice.reducer,
    [notificationSlice.name]: notificationSlice.reducer,
    [localizationSlice.name]: localizationSlice.reducer,
    [themeSlice.name]: themeSlice.reducer,
    [appDetailsSlice.name]: appDetailsSlice.reducer,
    [headerSlice.name]: headerSlice.reducer,
    [analyticsV1MetricsSlice.name]: analyticsV1MetricsSlice.reducer,
    [analyticsV2MetricsSlice.name]: analyticsV2MetricsSlice.reducer,
    [analyticsWidgetsSlice.name]: analyticsWidgetsSlice.reducer,
    [analyticsFilterSlice.name]: analyticsFilterSlice.reducer,
    [appListSlice.name]: appListSlice.reducer,
    [appValidationSlice.name]: appValidationSlice.reducer,
    [authenticatedImageSlice.name]: authenticatedImageSlice.reducer,
    [packageEditorSlice.name]: packageEditorSlice.reducer,
    [peoplePickerSlice.name]: peoplePickerSlice.reducer,
    [appPublishingSlice.name]: appPublishingSlice.reducer,
    [appTypeSlice.name]: appTypeSlice.reducer,
    [adaptiveCardSlice.name]: adaptiveCardSlice.reducer,
    [botSlice.name]: botSlice.reducer,
    [settingsSlice.name]: settingsSlice.reducer,
    [userContextSlice.name]: userContextSlice.reducer,
    [botEventSubscriptionSlice.name]: botEventSubscriptionSlice.reducer,
  });
}

export async function composeStore(deps: {
  services: DevPortalServices;
  router: Router;
}) {
  await immediate;
  const { services, router } = deps;

  const rootReducer = composeReducers();

  const themeCommandHandler = createThemeCommandHandler({
    logger: services.logger.extend('themeCommandHandler'),
    themePreference: services.userThemePreference,
    themeReader: services.themeReader,
  });

  const clientSwitchCommandHandler = createCientSwitchCommandHandler({
    logger: services.logger.extend('clientSwitchCommandHandler'),
    userClientPreference: services.userClientPreference,
  });

  const appDetailsCommandHandler = createAppDetailCommandHandler({
    logger: services.logger.extend('appDetailsCommandHandler'),
    appReader: services.appDetailsService,
    appWriter: services.appDetailsService,
    appPublishReader: services.appDetailsService,
    appPublishWriter: services.appDetailsService,
    appListReader: services.appListService,
    appLanguageWriter: services.appDetailsService,
    appOwnersWriter: services.appDetailsService,
    appIconWriter: services.appDetailsService,
  });

  const appAnalyticsCommandHandler = createAppAnalyticsCommandHandler({
    logger: services.logger.extend('appAnalyticsCommandHandler'),
    analyticsService: services.analyticsService,
  });

  const settingsCommandHandler = createSettingsCommandHandler({
    logger: services.logger.extend('settingsCommandHandler'),
    settingsService: services.settingsService,
  });

  const appListCommmandHandler = createAppListCommandHandler({
    logger: services.logger.extend('appListCommandHandler'),
    appListReader: services.appListService,
    appListItemDelete: services.appListService,
    appListItemWriter: services.appListService,
  });

  const appValidationCommandHandler = createAppValidationCommandHandler({
    logger: services.logger.extend('appValidationCommandHandler'),
    appValidation: services.appValidationService,
    appValidationWriter: services.appValidationService,
  });

  const packageEditorCommandHandler = createPackageEditorCommandHandler({
    logger: services.logger.extend('packageEditorCommandHandler'),
    packageEditor: services.packageEditorService
  });

  const headerCommandHandler = createHeaderCommandHandler({
    headerDataProvider: services.headerDataService,
  });

  const peoplePickerCommandHandler = createPeoplePickerCommands({
    logger: services.logger.extend('peoplePickerCommandHandler'),
    peoplePicker: services.peoplePickerService,
  });

  const themeEventSource = createThemeEventSource({
    logger: services.logger.extend('themeEventSource'),
    themeReader: services.themeReader,
  });

  const actionSourceMiddleware = combineActionSources({
    sources: [themeEventSource],
  });

  const localizationCommandHandler = createLocalizationCommandHandler({
    localeStringFetcher: services.localizationFetcher.getLocaleStrings.bind(
      services.localizationFetcher
    ),
  });

  const authenticatedImagesCommandHandler = createAuthenticatedImageCommandHandler({
    authenticatedImageFetcher: services.authenticatedImageFetcher,
    logger: services.logger.extend('authenticatedImagesCommandHandler'),
  });

  const adaptiveCardCommandHandler = createAdaptiveCardCommandHandler({
    logger: services.logger.extend('adaptiveCardCommandHandler'),
    adaptiveCardsReader: services.adaptiveCardService,
    adaptiveCardsWriter: services.adaptiveCardService,
  });

  const botCommandHandler = createBotCommandHandler({
    botProvider: services.botService,
    botWriter: services.botService,
    azureAdAppWriter: services.azureAdAppService,
    router,
    logger: services.logger.extend('createBotCommandHandler'),
  });
  const botEventSubscriptionCommandHandler = createBotEventSubscriptionCommandHandler({
    logger: services.logger.extend('botEventSubscriptionCommandHandler'),
    botEventSubscriptionProvider: services.botEventSubscriptionService,
    botEventSubscriptionWriter: services.botEventSubscriptionService,
  });

  const { middleware: commandHandlerMiddleware, commands } = combineHandlers({
    handlers: [
      localizationCommandHandler,
      themeCommandHandler,
      headerCommandHandler,
      appDetailsCommandHandler,
      appListCommmandHandler,
      appAnalyticsCommandHandler,
      appValidationCommandHandler,
      authenticatedImagesCommandHandler,
      packageEditorCommandHandler,
      peoplePickerCommandHandler,
      botCommandHandler,
      adaptiveCardCommandHandler,
      clientSwitchCommandHandler,
      settingsCommandHandler,
      botEventSubscriptionCommandHandler,
    ],
    onException: (e, handlerName) => {
      services.logger.error(
        `Unhandled exception in command handler %s: [%s] [%o]`,
        handlerName,
        e.message,
        e
      );
    },
  });

  // Usually would get from Result<store.getState> but inference there is getting hairy due to the rather aggressive UnknownAction type being common
  // TODO(nibeauli): update redux to v5 and this should get cleaner since they've fixed the bottom typing issues.
  const store = configureStore({
    reducer: rootReducer,
    // TODO(nibeauli): find a way of disabling 'thunk' without having the dispatch type fall through to Dispatch<UnknownAction>
    // or figure out a good way of getting Dispatch<Commands> flexible enough to support arbitrary work.
    // this _may_ not be a good idea https://phryneas.de/redux-typescript-no-discriminating-union
    // I've not reconciled it is a bad thing in scoped locations, but in these more core locations it might be true
    middleware: (gdm) =>
      gdm().concat(actionSourceMiddleware, commandHandlerMiddleware),
  });
  return { store, commandHandlerMiddleware, commands };
}

export type RootState =
  ReturnType<typeof composeReducers> extends Reducer<infer S> ? S : never;
export type AppCommand =
  ReturnType<typeof composeStore> extends Promise<infer S>
  ? // Allow any on the type due to the existing Middleware type declaration
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  S extends { commandHandlerMiddleware: Middleware<infer A, any, any> }
  ? A
  : never
  : never;

export const useSelector: TypedUseSelectorHook<RootState> = untypedUseSelector;

export type CommandDispatch = <TCommand extends AppCommand>(
  action: TCommand
) => TCommand;
// scope dispatched actions to only the 'command' subcategory. the others will be results of commands.
export const useDispatch = () => untypedUseDispatch() as CommandDispatch;

export type DevPortalCommands = PromisedReturnType<
  typeof composeStore
>['commands'];
